import { z } from "zod";
import {
  EstimatorSettingsSchema,
  ManagerSettingsSchema,
  SalesRepSettingsSchema,
  StandardSettingsSchema,
  type EstimatorSettings,
  type ManagerSettings,
  type SalesRepSettings,
  type StandardSettings,
} from "./statusReportSettings";

/**
 * The four canonical status-report templates. Managers and execs share the
 * manager template; every other user defaults to the standard template.
 */
export const StatusReportTemplateKey = {
  STANDARD: "standard",
  SALES_REP: "sales_rep",
  ESTIMATOR: "estimator",
  MANAGER: "manager",
} as const;
export type StatusReportTemplateKey =
  (typeof StatusReportTemplateKey)[keyof typeof StatusReportTemplateKey];

/** Zod schema for a status-report template key. */
export const StatusReportTemplateKeySchema = z.enum([
  StatusReportTemplateKey.STANDARD,
  StatusReportTemplateKey.SALES_REP,
  StatusReportTemplateKey.ESTIMATOR,
  StatusReportTemplateKey.MANAGER,
]);

/** Sentinel value for a user who has opted out of status reports entirely. */
export const STATUS_REPORT_OFF = "off";

/** A user's personal report selection: one template, or opted out. */
export type UserStatusReportSelection =
  | StatusReportTemplateKey
  | typeof STATUS_REPORT_OFF;

/** Zod schema for a user's personal report selection. */
export const UserStatusReportSelectionSchema = z.union([
  StatusReportTemplateKeySchema,
  z.literal(STATUS_REPORT_OFF),
]);

/** The status-report selection a user who has never chosen one holds. */
export const DEFAULT_STATUS_REPORT_SELECTION: UserStatusReportSelection =
  StatusReportTemplateKey.STANDARD;

/**
 * A template's admin-tunable thresholds, as persisted on the template row. The
 * shape is decided by the row's `templateKey`; reads narrow it by parsing
 * through the discriminated `StatusReportTemplateSchema`.
 */
export type StatusReportTemplateSettings =
  | StandardSettings
  | SalesRepSettings
  | EstimatorSettings
  | ManagerSettings;

/** Lifecycle status of a single scheduled report run (one template firing). */
export const StatusReportRunStatus = {
  PENDING: "pending",
  GENERATING: "generating",
  SENT: "sent",
  FAILED: "failed",
} as const;
export type StatusReportRunStatus =
  (typeof StatusReportRunStatus)[keyof typeof StatusReportRunStatus];

/** Delivery status of one recipient's copy within a run. */
export const StatusReportDeliveryStatus = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  SKIPPED: "skipped",
} as const;
export type StatusReportDeliveryStatus =
  (typeof StatusReportDeliveryStatus)[keyof typeof StatusReportDeliveryStatus];

/** Fields every template row carries, whatever its settings shape. */
const BaseStatusReportTemplateSchema = z.object({
  enabled: z.boolean(),
  reportContextBlurb: z.string().nullable(),
});

const StandardTemplateSchema = BaseStatusReportTemplateSchema.extend({
  templateKey: z.literal(StatusReportTemplateKey.STANDARD),
  settings: StandardSettingsSchema,
});

const SalesRepTemplateSchema = BaseStatusReportTemplateSchema.extend({
  templateKey: z.literal(StatusReportTemplateKey.SALES_REP),
  settings: SalesRepSettingsSchema,
});

const EstimatorTemplateSchema = BaseStatusReportTemplateSchema.extend({
  templateKey: z.literal(StatusReportTemplateKey.ESTIMATOR),
  settings: EstimatorSettingsSchema,
});

const ManagerTemplateSchema = BaseStatusReportTemplateSchema.extend({
  templateKey: z.literal(StatusReportTemplateKey.MANAGER),
  settings: ManagerSettingsSchema,
});

/**
 * One org status-report template row as exposed over the API. Discriminated on
 * `templateKey`, so narrowing the key narrows `settings` to that template's
 * own shape — the pairing is the type, not a runtime convention.
 */
export const StatusReportTemplateSchema = z.discriminatedUnion("templateKey", [
  StandardTemplateSchema,
  SalesRepTemplateSchema,
  EstimatorTemplateSchema,
  ManagerTemplateSchema,
]);
export type StatusReportTemplate = z.infer<typeof StatusReportTemplateSchema>;

/** Response for GET /status-report-templates: the org's four template rows. */
export const ListStatusReportTemplatesResponseSchema = z.object({
  templates: z.array(StatusReportTemplateSchema),
});
export type ListStatusReportTemplatesResponse = z.infer<
  typeof ListStatusReportTemplatesResponseSchema
>;

/**
 * Response for GET /status-report-templates/enabled-keys: the template keys
 * effectively enabled for the caller's org.
 */
export const EnabledStatusReportTemplateKeysResponseSchema = z.object({
  templateKeys: z.array(StatusReportTemplateKeySchema),
});
export type EnabledStatusReportTemplateKeysResponse = z.infer<
  typeof EnabledStatusReportTemplateKeysResponseSchema
>;

/**
 * Each template's settings schema, keyed by the template it belongs to — the
 * lookup for code paths that hold a key at runtime (the write-side parse).
 */
export const TEMPLATE_SETTINGS_SCHEMAS = {
  [StatusReportTemplateKey.STANDARD]: StandardSettingsSchema,
  [StatusReportTemplateKey.SALES_REP]: SalesRepSettingsSchema,
  [StatusReportTemplateKey.ESTIMATOR]: EstimatorSettingsSchema,
  [StatusReportTemplateKey.MANAGER]: ManagerSettingsSchema,
} as const;

/** Fields every PUT body may carry; `settings` is added per template. */
const BaseUpdateTemplateBodySchema = z.object({
  enabled: z.boolean().optional(),
  reportContextBlurb: z.string().nullable().optional(),
});

/**
 * Body schemas for PUT /status-report-templates/:templateKey, keyed by the
 * template the route is addressing. Fields are optional (partial update), but
 * `settings` is the addressed template's own schema — a mismatched bag fails at
 * the route rather than deeper in.
 */
export const UPDATE_TEMPLATE_BODY_SCHEMAS = {
  [StatusReportTemplateKey.STANDARD]: BaseUpdateTemplateBodySchema.extend({
    settings: StandardSettingsSchema.optional(),
  }),
  [StatusReportTemplateKey.SALES_REP]: BaseUpdateTemplateBodySchema.extend({
    settings: SalesRepSettingsSchema.optional(),
  }),
  [StatusReportTemplateKey.ESTIMATOR]: BaseUpdateTemplateBodySchema.extend({
    settings: EstimatorSettingsSchema.optional(),
  }),
  [StatusReportTemplateKey.MANAGER]: BaseUpdateTemplateBodySchema.extend({
    settings: ManagerSettingsSchema.optional(),
  }),
} as const;

/**
 * The PUT body for one template key, with that template's settings shape. This
 * is the schema's *input* type, so fields carrying a default stay optional for
 * the caller and zod materializes them — a partial bag writes a full one.
 */
export type UpdateStatusReportTemplateBody<
  K extends StatusReportTemplateKey = StatusReportTemplateKey,
> = z.input<(typeof UPDATE_TEMPLATE_BODY_SCHEMAS)[K]>;
